import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { useTelegram, useBackButton } from '@/shared/lib/telegram'
import { AuthGuard } from '@/shared/lib/router'
import { useOrderById } from '@/features/orders/api/use-orders'
import { Button, Skeleton } from '@/shared/ui'
import { CheckCircle2, Package, ArrowRight, ShoppingBag } from 'lucide-react'
import { formatPrice, formatDate } from '@/shared/lib/format'

const orderSuccessSearchSchema = z.object({
  orderId: z.string().optional(),
})

export const Route = createFileRoute('/order-success')({
  validateSearch: orderSuccessSearchSchema,
  component: OrderSuccessPage,
})

/**
 * Страница успешного оформления заказа
 * 
 * Показывает информацию о созданном заказе.
 * Защищена авторизацией через AuthGuard.
 */
function OrderSuccessPage() {
  return (
    <AuthGuard>
      <OrderSuccessContent />
    </AuthGuard>
  )
}

/**
 * Внутренний компонент с контентом страницы
 * (отделён для правильной работы хуков)
 */
function OrderSuccessContent() {
  const { hapticFeedback } = useTelegram()
  const navigate = useNavigate()
  const { orderId } = Route.useSearch()
  
  // Нативная кнопка "Назад" → каталог
  useBackButton(true, () => navigate({ to: '/catalog/$section', params: { section: 'tea' } }))

  const { data: order, isLoading } = useOrderById(orderId)

  const handleGoToCatalog = () => {
    hapticFeedback?.impactOccurred('light')
    navigate({ to: '/catalog/$section', params: { section: 'tea' } })
  }

  const handleGoToOrders = () => {
    hapticFeedback?.impactOccurred('light')
    navigate({ to: '/orders' })
  }

  const handleViewOrder = () => {
    if (!orderId) return
    hapticFeedback?.impactOccurred('light')
    navigate({ to: '/order/$orderId', params: { orderId } })
  }

  // Загрузка
  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-12 flex flex-col items-center justify-center min-h-[60vh]">
        <Skeleton className="h-16 w-16 rounded-full mb-4" />
        <Skeleton className="h-8 w-64 mb-2" />
        <Skeleton className="h-4 w-48" />
      </div>
    )
  }

  // Получаем текст способа доставки
  const getDeliveryTypeText = (type: string | undefined) => {
    switch (type) {
      case 'pickup': return 'Самовывоз'
      case 'cdek_pvz': return 'СДЭК (пункт выдачи)'
      case 'cdek_door': return 'СДЭК (курьер)'
      default: return 'Доставка'
    }
  }

  const isCash = order?.paymentMethod === 'cash'

  return (
    <div className="container mx-auto px-4 py-12 flex flex-col items-center justify-center min-h-[60vh]">
      <div className="text-center space-y-6 max-w-md animate-in fade-in slide-in-from-bottom-4 duration-500">
        {/* Иконка успеха */}
        <div className="flex justify-center">
          <div className={`h-20 w-20 rounded-full flex items-center justify-center animate-in zoom-in duration-300 ${isCash ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-green-100 dark:bg-green-900/30'}`}>
            <CheckCircle2 className={`h-12 w-12 ${isCash ? 'text-blue-500' : 'text-green-500'}`} />
          </div>
        </div>

        {/* Заголовок */}
        <div>
          <h1 className="text-2xl font-bold text-ink dark:text-white mb-2">
            {isCash ? '🏪 Заказ оформлен!' : '✅ Заказ оплачен!'}
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            {isCash
              ? `Оплатите ${order ? formatPrice(order.totalAmount) : ''} при получении в магазине`
              : 'Ваш заказ оплачен и передан в обработку'}
          </p>
        </div>

        {/* Информация о заказе */}
        {order && (
          <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm p-6 space-y-4 text-left">
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-4">
              <Package className="h-5 w-5" />
              <span className="font-medium">Информация о заказе</span>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-500">Номер заказа:</span>
                <span className="font-semibold text-ink dark:text-white">
                  #{order.id.slice(0, 8)}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-500">Дата:</span>
                <span className="font-medium text-ink dark:text-white">
                  {formatDate(order.createdAt)}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-500">Сумма:</span>
                <span className="font-bold text-primary text-lg">
                  {formatPrice(order.totalAmount)}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-500">Способ:</span>
                <span className="font-medium text-ink dark:text-white">
                  {getDeliveryTypeText(order.deliveryType)}
                </span>
              </div>
            </div>

            {/* Кнопка просмотра */}
            <Button
              variant="secondary"
              className="w-full mt-4"
              onClick={handleViewOrder}
            >
              Подробнее о заказе
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Кнопки */}
        <div className="space-y-3 w-full">
          <Button 
            className="w-full" 
            onClick={handleGoToCatalog}
          >
            <ShoppingBag className="w-4 h-4 mr-2" />
            Продолжить покупки
          </Button>
          
          <Button 
            variant="secondary" 
            className="w-full"
            onClick={handleGoToOrders}
          >
            Все заказы
          </Button>
        </div>
      </div>
    </div>
  )
}
